<template>
  <div
    class="relative flex flex-1 flex-col overflow-hidden"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <DropZoneOverlay :visible="isDragging" />

    <div
      v-if="session.data"
      class="border-b border-gray-100 px-6 py-2 text-right text-xs text-gray-500"
    >
      {{ formatCredits(session.data.creditsUsed) }} credits used
    </div>

    <!-- Messages Container -->
    <div
      ref="messagesContainer"
      class="custom-table-scroll flex flex-1 flex-col gap-3 overflow-y-auto bg-gray-50 p-4"
    >
      <div
        v-for="message in displayMessages"
        :key="message.id"
        class="flex"
        :class="{
          'justify-end': message.role === 'user',
          'justify-start': message.role !== 'user',
        }"
      >
        <!-- AI message -->
        <div v-if="message.role === 'assistant'" class="flex max-w-3xl gap-2">
          <div class="mt-1 flex-shrink-0">
            <FyrAiLogo :size="20" class="text-purple-600" />
          </div>
          <div>
            <div class="rounded-lg bg-white px-4 py-3 shadow-sm">
              <!-- eslint-disable vue/no-v-html -->
              <div
                class="ai-chat-content prose prose-sm max-w-none wrap-break-word text-gray-900 [&_a]:break-all"
                v-html="renderMarkdown(parseAIContent(message.content).text)"
              ></div>
              <!-- eslint-enable vue/no-v-html -->
              <AIContextBox
                :entities="parseAIContent(message.content).entities"
              />
              <div
                v-if="message.toolCalls && message.toolCalls.length > 0"
                class="mt-2 space-y-1"
              >
                <AIToolCallSummary
                  v-for="(call, idx) in message.toolCalls"
                  :key="idx"
                  :name="call.name"
                  status="complete"
                />
              </div>
            </div>
            <!-- Quick-reply suggestion chips (only on last assistant message, when not streaming) -->
            <div
              v-if="
                isLastAssistantMessage(message.id) &&
                !streamState.isStreaming &&
                parseAIContent(message.content).suggestions.length > 0
              "
              class="mt-2 flex flex-wrap gap-2"
            >
              <button
                v-for="(suggestion, idx) in parseAIContent(message.content)
                  .suggestions"
                :key="idx"
                class="rounded-full border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-medium text-purple-700 transition hover:bg-purple-600 hover:text-white"
                :disabled="isTokenLimitExceeded"
                @click="handleSuggestionClick(suggestion)"
              >
                {{ suggestion }}
              </button>
            </div>
          </div>
        </div>

        <!-- User message -->
        <div
          v-else-if="message.role === 'user'"
          class="flex max-w-3xl flex-col items-end gap-1"
        >
          <div class="rounded-lg bg-purple-600 px-4 py-3 text-white shadow-sm">
            <div class="text-sm whitespace-pre-wrap">
              {{ message.content }}
            </div>
            <!-- Attached images -->
            <div
              v-if="message.attachments?.length"
              class="mt-2 flex flex-wrap gap-2"
            >
              <a
                v-for="(att, idx) in message.attachments"
                :key="idx"
                :href="att.url"
                target="_blank"
                rel="noopener"
                class="block"
              >
                <img
                  :src="att.url"
                  :alt="att.filename || 'Attached image'"
                  class="max-h-40 max-w-60 rounded-md border border-purple-400"
                  loading="lazy"
                />
              </a>
            </div>
          </div>
          <div class="text-xs text-gray-400">
            {{ formatTime(message.createdAt) }}
          </div>
        </div>

        <!-- Tool messages are hidden — the AI summarises results in its response -->
      </div>

      <!-- Streaming indicator -->
      <div v-if="streamState.isStreaming" class="flex gap-2">
        <div class="mt-1 flex-shrink-0">
          <FyrAiLogo :size="20" class="text-purple-600" sparkle-loop />
        </div>
        <div class="rounded-lg bg-white px-4 py-3 shadow-sm">
          <div class="flex flex-col gap-2">
            <!-- Current message being streamed -->
            <!-- Streamed message content -->
            <!-- eslint-disable vue/no-v-html -->
            <div
              v-if="streamState.currentMessage"
              class="prose prose-sm max-w-none wrap-break-word text-gray-900 [&_a]:break-all"
              v-html="
                renderMarkdown(stripMetaBlocks(streamState.currentMessage))
              "
            ></div>
            <!-- eslint-enable vue/no-v-html -->

            <!-- Activity log (always visible during streaming when no message) -->
            <div v-if="!streamState.currentMessage" class="space-y-2">
              <!-- Completed tool calls -->
              <div v-if="completedToolCalls.length > 0" class="space-y-0.5">
                <AIToolCallSummary
                  v-for="(call, idx) in completedToolCalls"
                  :key="`done-${idx}`"
                  :name="call.name"
                  status="complete"
                />
              </div>

              <!-- Current activity: running tool or thinking -->
              <div class="flex items-center gap-2 text-sm text-gray-600">
                <Icon
                  name="spinner-third-duotone"
                  class="size-4 animate-spin text-purple-600"
                />
                <span>{{
                  runningToolCall ? runningToolLabel : thinkingMessage
                }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Error message -->
      <div
        v-if="messages.error || streamState.error"
        class="rounded-md bg-red-50 p-3 text-sm text-red-800"
      >
        <p class="font-semibold">Error:</p>
        <p>{{ streamState.error || messages.error?.message }}</p>
      </div>
    </div>

    <div
      v-if="isTokenLimitExceeded"
      class="border-t border-gray-200 bg-orange-50 px-4 py-3 text-sm text-orange-800"
    >
      <p class="font-semibold">Monthly AI usage limit reached</p>
      <p>
        The limit will reset in {{ usage.data?.daysUntilRenewal }}
        {{ usage.data?.daysUntilRenewal === 1 ? "day" : "days" }}.
      </p>
    </div>

    <!-- Input Area -->
    <div class="border-t border-gray-200 p-4">
      <!-- Pending attachment previews -->
      <PendingAttachmentList
        class="mb-2"
        :attachments="pendingAttachments"
        :warning="attachmentWarning"
        @remove="removePendingAttachment"
      />

      <!-- The session payload carries the levels its model accepts; an empty
        list means the model takes no effort setting, so the control would be
        a choice with no effect. Offering exactly the served levels also keeps
        `Minimal` off models that reject it. -->
      <div v-if="supportedEfforts.length" class="mb-2">
        <ReasoningEffortSelect
          v-model="chatEffort"
          :options="supportedEfforts"
          :disabled="
            isSending || streamState.isStreaming || isTokenLimitExceeded
          "
        />
      </div>

      <div class="flex gap-2">
        <!-- Hidden file input -->
        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg"
          multiple
          class="hidden"
          @change="handleFileSelect"
        />
        <!-- Attach image button -->
        <button
          type="button"
          class="self-end rounded-md p-2 text-gray-400 hover:text-purple-600 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="
            isSending || streamState.isStreaming || isTokenLimitExceeded
          "
          title="Attach image"
          @click="fileInput?.click()"
        >
          <Icon name="image" class="size-5" />
        </button>
        <textarea
          ref="messageInput"
          v-model="newMessage"
          rows="2"
          class="flex-1 rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
          placeholder="Continue the conversation..."
          :disabled="
            isSending || streamState.isStreaming || isTokenLimitExceeded
          "
          @keydown.enter.exact.prevent="handleSend"
          @paste="handlePaste"
        />
        <button
          v-if="streamState.isStreaming"
          class="self-end rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          @click="handleCancel"
        >
          <Icon name="times-circle" class="size-4" />
        </button>
        <button
          v-else
          class="self-end rounded-md bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="
            isSending ||
            (!newMessage.trim() && pendingAttachments.length === 0) ||
            isTokenLimitExceeded
          "
          @click="handleSend"
        >
          <Icon name="arrow-circle-right" class="size-4" />
        </button>
      </div>
      <p class="mt-1 text-xs text-gray-500">
        {{
          streamState.isStreaming
            ? "Click the button to cancel"
            : "Press Enter to send, Shift+Enter for new line"
        }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from "vue";
import Icon from "@/components/Core/Icon.vue";
import FyrAiLogo from "@/components/Core/FyrAiLogo.vue";
import {
  useAISessionQuery,
  useAISessionMessagesQuery,
  type AIAgentMessage,
  type ChatAttachment,
} from "@/services/ai_agent";
import ReasoningEffortSelect from "@/components/AIAgent/ReasoningEffortSelect.vue";
import { useAIChatSlideover } from "@/composables/useAIChatSlideover";
import {
  useAIAgentStream,
  getApiErrorMessage,
} from "@/composables/useAIAgentStream";
import { useAIContext } from "@/composables/useAIContext";
import {
  formatCredits,
  useIsAITokenLimitExceeded,
} from "@/services/ai_token_usage";
import { DateTime } from "luxon";
import { renderMarkdown, parseAIContent } from "@/composables/useMarkdown";
import {
  usePendingAttachments,
  IMAGE_ONLY_FALLBACK_MESSAGE,
} from "@/composables/usePendingAttachments";
import { downloadFile } from "@/utils/download";
import AIContextBox from "@/components/AIAgent/AIContextBox.vue";
import AIToolCallSummary from "@/components/AIAgent/AIToolCallSummary.vue";
import DropZoneOverlay from "@/components/AIAgent/DropZoneOverlay.vue";
import PendingAttachmentList from "@/components/AIAgent/PendingAttachmentList.vue";

const props = defineProps({
  sessionId: {
    type: Number,
    required: true,
  },
  initialMessage: {
    type: String as unknown as () => string | null,
    default: null,
  },
  initialAttachments: {
    type: Array as () => ChatAttachment[] | undefined,
    default: undefined,
  },
});

const emit = defineEmits({
  "update:initialMessage": (value: string | null) =>
    typeof value === "string" || value === null,
  "update:initialAttachments": (_value: ChatAttachment[] | undefined) => true,
});

const {
  pendingAttachments,
  attachmentWarning,
  isDragging,
  handleFileSelect,
  handlePaste,
  onDragOver,
  onDragLeave,
  onDrop,
  removePendingAttachment,
  uploadPendingAttachments,
  clearPendingAttachments,
} = usePendingAttachments();

const sessionIdRef = computed(() => props.sessionId);
const session = useAISessionQuery(sessionIdRef);
const messages = useAISessionMessagesQuery(sessionIdRef);
const {
  state: streamState,
  sendMessageStreaming,
  cancelSession,
} = useAIAgentStream(sessionIdRef);
const { context } = useAIContext();
const { isExceeded: isTokenLimitExceeded, usage } = useIsAITokenLimitExceeded();
const { chatEffort } = useAIChatSlideover();

// Served rather than derived from the model name: the effort vocabulary
// differs within the GPT-5 family, which a client-side list of
// effort-capable models cannot express. Older cached payloads have no such
// field, so an absent value means "no control" rather than "all levels".
const supportedEfforts = computed(
  () => session.data?.supportedReasoningEfforts ?? [],
);

const THINKING_MESSAGES = [
  "Analyzing your data...",
  "Digging into the numbers...",
  "Looking at the details...",
  "Crunching the metrics...",
  "Connecting the dots...",
  "Finding insights...",
  "Almost there...",
  "Putting it all together...",
  "One more look...",
  "Wrapping up the analysis...",
];

const thinkingMessage = computed(() => {
  const iteration = streamState.value.iteration;
  const idx = Math.min(iteration, THINKING_MESSAGES.length - 1);
  return THINKING_MESSAGES[idx];
});

const completedToolCalls = computed(() =>
  streamState.value.toolCalls.filter((tc) => tc.status === "complete"),
);

const runningToolCall = computed(() =>
  streamState.value.toolCalls.find((tc) => tc.status === "running"),
);

const TOOL_RUNNING_LABELS: Record<string, string> = {
  list_entities: "Looking up entities...",
  get_metrics: "Analyzing performance...",
  get_search_terms: "Analyzing search terms...",
  get_website_metrics: "Analyzing website traffic...",
  get_search_console: "Fetching search console data...",
  get_social_page_metrics: "Analyzing social media...",
  get_social_posts: "Analyzing social posts...",
  get_ecommerce_metrics: "Fetching e-commerce data...",
  get_audience: "Analyzing audience...",
  get_heatmap: "Computing heatmap...",
  get_share_of_search: "Computing share of search...",
  get_competitor_followers: "Fetching competitor followers...",
  get_competitor_metrics_comparison: "Comparing against competitors...",
  get_reference: "Reading reference docs...",
  get_available_metrics: "Checking available metrics...",
  get_conversion_metrics: "Analyzing conversions...",
  create_dashboard: "Creating dashboard...",
  update_dashboard: "Updating dashboard...",
  create_widget_simple: "Adding widget...",
  create_goal_or_kpi_widget: "Adding goal/KPI widget...",
  manage_tab: "Updating tabs...",
  manage_widgets: "Updating widgets...",
  manage_competitor: "Managing competitor...",
  manage_goal: "Managing goal...",
  manage_kpi: "Managing KPI...",
  manage_annotation: "Managing annotation...",
};

const runningToolLabel = computed(() => {
  if (!runningToolCall.value) return "";
  return (
    TOOL_RUNNING_LABELS[runningToolCall.value.name] ??
    runningToolCall.value.name.replace(/_/g, " ") + "..."
  );
});

const messagesContainer = ref<HTMLElement | null>(null);
const messageInput = ref<HTMLTextAreaElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const newMessage = ref("");
const isSending = ref(false);
const pendingUserMessage = ref<string | null>(null);
const pendingUserAttachments = ref<ChatAttachment[]>([]);

// Filter out system messages from display, and append pending user message
const displayMessages = computed<AIAgentMessage[]>(() => {
  let serverMessages = messages.data
    ? messages.data.filter((m) => m.role !== "system")
    : [];

  // While streaming, hide intermediate assistant messages (those with
  // toolCalls) from the current turn — the streaming indicator already
  // shows tool-call progress.  Without this, a mid-stream cache refresh
  // (e.g. refetchOnWindowFocus on tab switch) pulls intermediate
  // messages and shows tool calls in both the message list and the
  // streaming indicator.
  if (streamState.value.isStreaming && serverMessages.length > 0) {
    let lastUserIdx = -1;
    for (let i = serverMessages.length - 1; i >= 0; i--) {
      if (serverMessages[i].role === "user") {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx >= 0) {
      serverMessages = serverMessages.filter(
        (m, i) =>
          i <= lastUserIdx ||
          !(m.role === "assistant" && m.toolCalls && m.toolCalls.length > 0),
      );
    }
  }

  if (pendingUserMessage.value) {
    // Check whether the server already persisted this user message
    // (refetch can complete before finally{} clears pendingUserMessage).
    // Only compare the last user message to avoid false positives when
    // the user intentionally sends the same text twice.
    const lastUserMsg = serverMessages.findLast((m) => m.role === "user");
    function attUrls(atts?: ChatAttachment[]) {
      return (atts ?? [])
        .map((a) => a.url)
        .sort()
        .join("|");
    }
    const alreadySaved =
      lastUserMsg?.content === pendingUserMessage.value &&
      attUrls(lastUserMsg?.attachments) ===
        attUrls(pendingUserAttachments.value);
    if (!alreadySaved) {
      return [
        ...serverMessages,
        {
          id: -1,
          role: "user",
          content: pendingUserMessage.value,
          attachments:
            pendingUserAttachments.value.length > 0
              ? pendingUserAttachments.value
              : undefined,
          tokensUsed: 0,
          createdAt: new Date().toISOString(),
        },
      ];
    }
  }
  return serverMessages;
});

// Format timestamp
function formatTime(timestamp: string): string {
  return DateTime.fromISO(timestamp).toFormat("h:mm a");
}

// Scroll the messages container to the bottom.  Does a synchronous
// scroll first (forces the browser to reflow immediately so new
// content is measured), then a deferred scroll via RAF to catch any
// content added in later microtasks / Vue patches.
function scrollToBottom() {
  const el = messagesContainer.value;
  if (!el) return;
  el.scrollTop = el.scrollHeight;
  requestAnimationFrame(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

// Auto-scroll to bottom when new messages arrive
watch(
  () => displayMessages.value.length,
  async () => {
    await nextTick();
    scrollToBottom();
  },
  { immediate: true },
);

// Scroll to bottom and focus input on mount
watch(
  () => props.sessionId,
  async () => {
    await nextTick();
    await nextTick();
    scrollToBottom();
    messageInput.value?.focus();
  },
  { immediate: true },
);

// Auto-scroll when streaming content updates
watch(
  () => streamState.value.currentMessage,
  async () => {
    await nextTick();
    scrollToBottom();
  },
);

// Auto-scroll when streaming starts or stops, and focus input when done
watch(
  () => streamState.value.isStreaming,
  async (isStreaming) => {
    await nextTick();
    scrollToBottom();
    if (!isStreaming) {
      messageInput.value?.focus();
    }
  },
);

// Auto-scroll when tool calls update
watch(
  () => streamState.value.toolCalls.length,
  async () => {
    await nextTick();
    scrollToBottom();
  },
);

// Auto-send initial message passed from parent via prop
watch(
  () => props.initialMessage,
  async (message) => {
    if (!message || !props.sessionId || streamState.value.isStreaming) return;
    const attachments = props.initialAttachments;
    pendingUserMessage.value = message;
    pendingUserAttachments.value = attachments ?? [];
    const beforeId = lastAssistantId();
    try {
      const result = await sendMessageStreaming(
        message,
        context.value,
        attachments,
        chatEffort.value,
      );
      emit("update:initialMessage", null);
      emit("update:initialAttachments", undefined);
      if (!result.messagesAlreadyCached) {
        await messages.refetch();
        const newId = lastAssistantId();
        if (newId === null || newId === beforeId) {
          startBackgroundPoll(beforeId);
        }
      }
      session.refetch();
    } catch (error) {
      streamState.value.error = getApiErrorMessage(
        error,
        "Failed to send message",
      );
    } finally {
      pendingUserMessage.value = null;
      pendingUserAttachments.value = [];
    }
  },
  { immediate: true },
);

function stripMetaBlocks(content: string): string {
  return content
    .replace(/---suggestions\s*\n[\s\S]*?(\n---\s*$|$)/m, "")
    .replace(/---context\s*\n[\s\S]*?(\n---\s*$|$)/m, "")
    .replace(/\n---(?:context|suggestions)[\s\S]*$/m, "")
    .replace(/\n---\s*$/m, "")
    .trimEnd();
}

function isLastAssistantMessage(messageId: number): boolean {
  const assistantMessages = displayMessages.value.filter(
    (m) => m.role === "assistant",
  );
  return (
    assistantMessages.length > 0 &&
    assistantMessages[assistantMessages.length - 1].id === messageId
  );
}

// After streaming resolves, the final assistant message may not be in
// the DB yet (backend race: session status updates before the last
// message is committed).  Start a background poll that keeps
// refetching until the response appears — this way handleSend
// unblocks immediately and the response shows up automatically.
const BACKGROUND_POLL_INTERVAL = 2000;
const BACKGROUND_POLL_MAX_DURATION = 60_000;
let backgroundPollTimer: ReturnType<typeof setInterval> | null = null;

function lastAssistantId(): number | null {
  if (!messages.data) return null;
  for (let i = messages.data.length - 1; i >= 0; i--) {
    const m = messages.data[i];
    if (m.role === "assistant" && (!m.toolCalls || m.toolCalls.length === 0)) {
      return m.id;
    }
  }
  return null;
}

function stopBackgroundPoll() {
  if (backgroundPollTimer) {
    clearInterval(backgroundPollTimer);
    backgroundPollTimer = null;
  }
}

function startBackgroundPoll(beforeId: number | null) {
  stopBackgroundPoll();
  const start = Date.now();
  let inFlight = false;
  backgroundPollTimer = setInterval(async () => {
    if (inFlight) return;
    if (Date.now() - start > BACKGROUND_POLL_MAX_DURATION) {
      stopBackgroundPoll();
      return;
    }
    inFlight = true;
    try {
      await messages.refetch();
      const newId = lastAssistantId();
      if (newId !== null && newId !== beforeId) {
        stopBackgroundPoll();
        session.refetch();
      }
    } finally {
      inFlight = false;
    }
  }, BACKGROUND_POLL_INTERVAL);
}

function handleSuggestionClick(suggestion: string) {
  newMessage.value = suggestion;
  handleSend();
}

async function handleSend() {
  const hasText = newMessage.value.trim().length > 0;
  const hasAttachments = pendingAttachments.value.length > 0;
  if (
    (!hasText && !hasAttachments) ||
    !props.sessionId ||
    isSending.value ||
    streamState.value.isStreaming
  ) {
    return;
  }

  isSending.value = true;

  // Upload attachments first
  let attachments: ChatAttachment[] | undefined;
  if (hasAttachments) {
    const uploaded = await uploadPendingAttachments();
    if (!uploaded) {
      isSending.value = false;
      return; // upload failed, don't send
    }
    attachments = uploaded;
  }

  const message = newMessage.value.trim() || IMAGE_ONLY_FALLBACK_MESSAGE;
  newMessage.value = "";
  pendingUserMessage.value = message;
  pendingUserAttachments.value = attachments ?? [];
  clearPendingAttachments();

  try {
    const beforeId = lastAssistantId();
    const result = await sendMessageStreaming(
      message,
      context.value,
      attachments,
      chatEffort.value,
    );
    if (result.messagesAlreadyCached) {
      // Polling already pushed fresh messages into the query cache
    } else {
      // Do one immediate refetch, then start background polling if
      // the response hasn't appeared yet.
      await messages.refetch();
      const newId = lastAssistantId();
      if (newId === null || newId === beforeId) {
        startBackgroundPoll(beforeId);
      }
    }
    session.refetch();
  } catch (error) {
    streamState.value.error = getApiErrorMessage(
      error,
      "Failed to send message",
    );
  } finally {
    isSending.value = false;
    pendingUserMessage.value = null;
    pendingUserAttachments.value = [];
  }
}

async function handleCancel() {
  if (!props.sessionId) return;

  try {
    await cancelSession();
    messages.refetch();
    session.refetch();
  } catch (error) {
    console.error("Failed to cancel session:", error);
  }
}

// --- Code block copy/download buttons (event delegation) ---

function enhanceCodeBlocks(container: HTMLElement) {
  const codeBlocks = container.querySelectorAll("pre > code");
  for (const codeEl of codeBlocks) {
    const pre = codeEl.parentElement!;
    if (pre.dataset.enhanced) continue;
    pre.dataset.enhanced = "true";
    pre.classList.add("group", "relative");

    const lang = detectLanguage(codeEl);

    const toolbar = document.createElement("div");
    toolbar.className = "absolute top-1 right-1 hidden gap-1 group-hover:flex";
    toolbar.appendChild(createToolbarButton("Copy", "copy"));
    toolbar.appendChild(createToolbarButton("Download", "download"));

    pre.dataset.lang = lang;
    pre.appendChild(toolbar);
  }
}

function detectLanguage(codeEl: Element): string {
  for (const cls of codeEl.classList) {
    if (cls.startsWith("language-")) return cls.slice(9);
  }
  const text = (codeEl.textContent || "").trim();
  if (
    (text.startsWith("{") && text.endsWith("}")) ||
    (text.startsWith("[") && text.endsWith("]"))
  ) {
    return "json";
  }
  return "text";
}

function createToolbarButton(label: string, action: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.textContent = label;
  btn.dataset.codeAction = action;
  btn.className =
    "rounded bg-gray-700/80 px-2 py-0.5 text-[10px] text-white hover:bg-gray-600";
  return btn;
}

function handleCodeBlockAction(e: MouseEvent) {
  const btn = (e.target as HTMLElement).closest<HTMLButtonElement>(
    "[data-code-action]",
  );
  if (!btn) return;
  e.stopPropagation();

  const pre = btn.closest("pre");
  const codeEl = pre?.querySelector("code");
  if (!pre || !codeEl) return;

  const text = codeEl.textContent || "";
  const action = btn.dataset.codeAction;

  if (action === "copy") {
    navigator.clipboard.writeText(text);
    btn.textContent = "Copied!";
    setTimeout(() => {
      btn.textContent = "Copy";
    }, 1500);
  } else if (action === "download") {
    const lang = pre.dataset.lang || "text";
    const ext = lang === "json" ? ".json" : ".txt";
    const mime = lang === "json" ? "application/json" : "text/plain";
    downloadFile(text, `ai-output${ext}`, mime);
  }
}

onMounted(() => {
  messagesContainer.value?.addEventListener("click", handleCodeBlockAction);
});

onBeforeUnmount(() => {
  messagesContainer.value?.removeEventListener("click", handleCodeBlockAction);
  stopBackgroundPoll();
});

// Enhance code blocks when messages change or streaming stops
watch(
  [() => displayMessages.value.length, () => streamState.value.isStreaming],
  async () => {
    await nextTick();
    if (!messagesContainer.value) return;
    const contentDivs =
      messagesContainer.value.querySelectorAll(".ai-chat-content");
    for (const div of contentDivs) {
      enhanceCodeBlocks(div as HTMLElement);
    }
  },
);
</script>
